import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, Plus } from 'lucide-react'
import api from '../../services/api'

const STATUS_BADGE: Record<string, string> = {
  scheduled: 'badge-medium', in_progress: 'badge-medium', completed: 'badge-low', cancelled: 'badge-high',
}
const BAND_COLOUR: Record<string, string> = {
  pass: 'var(--green)', marginal: 'var(--amber)', fail: 'var(--red)',
}

export default function AuditsList() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ status: '', audit_type: '' })

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.status) params.set('status', filter.status)
      if (filter.audit_type) params.set('audit_type', filter.audit_type)
      const { data: res } = await api.get(`/audits?${params}`)
      setData(res.data)
      setTotal(res.total)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Audits</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{total} total</p>
        </div>
        <div className="flex gap-2">
          <Link to="/audits/templates" className="btn btn-secondary">Templates</Link>
          <Link to="/audits/new" className="btn btn-primary"><Plus size={16} /> New Audit</Link>
        </div>
      </div>

      <div className="card p-4 flex flex-wrap gap-3">
        <select className="w-auto" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="w-auto" value={filter.audit_type} onChange={e => setFilter(f => ({ ...f, audit_type: e.target.value }))}>
          <option value="">All types</option>
          <option value="whs">WHS</option>
          <option value="environmental">Environmental</option>
          <option value="quality">Quality</option>
          <option value="contractor">Contractor</option>
          <option value="fire_safety">Fire Safety</option>
          <option value="emergency">Emergency</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <ClipboardCheck size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No audits found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Schedule an audit or create audit templates first</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Template</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Type</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Auditor</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Scheduled</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Score</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map(a => (
                <tr key={a.id} className="hover:bg-gray-50 transition-all" style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3">
                    <Link to={`/audits/${a.id}`} className="font-mono text-xs font-medium" style={{ color: 'var(--accent)' }}>{a.audit_number}</Link>
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{a.template_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="badge badge-medium capitalize">{a.audit_type?.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{a.site_name || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{a.auditor_name}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {a.scheduled_date ? new Date(a.scheduled_date).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {a.overall_score !== null ? (
                      <span className="font-bold text-sm" style={{ color: BAND_COLOUR[a.score_band] || 'var(--text)' }}>
                        {a.overall_score}%
                      </span>
                    ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_BADGE[a.status] || ''} capitalize`}>{a.status?.replace('_', ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
